import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormHelperText from '@mui/material/FormHelperText';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { observer } from 'mobx-react-lite';
import React, { ReactElement } from 'react';

type SelectMultipleInputProps = {
  errors?: string;
  defaultValue?: string[];
  value: string[];
  disabled?: boolean;
  tabIndexVal?: number;
  onChangeText?: (src: string[]) => void;
  onBlurFn?: (src: any) => void;
  isEditable?: boolean;
  name: string;
  isRefreshValue?: boolean;
  isFormSubmittedOnce?: boolean;
  inputRefObj?: React.Ref<any>;
  optionsList: Array<{
    label: string;
    value: string;
    disabled?: boolean;
    optionClassName?: any;
  }>;
  selectClassName?: string;
  iconsClassName?: string;
  placeHolder?: string;
  size?: 'small' | 'medium';
  sortByOptionDisable?: boolean;
  defaultOptionLabel?: string;
};

export const SelectMultipleInput = observer(
  (props: SelectMultipleInputProps): ReactElement => {
    const {
      errors,
      defaultValue = [],
      value,
      tabIndexVal = 0,
      onChangeText,
      onBlurFn,
      isEditable = false,
      name,
      isRefreshValue = false,
      isFormSubmittedOnce,
      inputRefObj,
      optionsList,
      selectClassName,
      iconsClassName,
      placeHolder,
      size = 'medium',
      sortByOptionDisable = false,
      defaultOptionLabel,
      disabled,
    } = props;

    const classes = useStyles();
    const [inputVal, setInputVal] = React.useState<string[]>(value);
    const [showLocalError, setShowLocalError] = React.useState(true);

    const onValueChange = React.useCallback(
      (val: string[]) => {
        setInputVal(val);
        setShowLocalError(val.length === 0);
        onChangeText && onChangeText(val);
      },
      [onChangeText],
    );

    const onBlur = (event: any) => {
      setShowLocalError(true);
      if (onBlurFn) {
        onBlurFn(event);
      }
    };

    React.useEffect(() => {
      if (isRefreshValue && inputVal !== value) {
        setInputVal(value);
        setShowLocalError(value.length === 0);
      }
    }, [inputVal, value, isRefreshValue]);

    const showError = isFormSubmittedOnce || showLocalError;

    return (
      <>
        <Select
          multiple
          value={inputVal}
          size={size ? size : undefined}
          displayEmpty
          name={name}
          classes={iconsClassName ? { icon: iconsClassName } : {}}
          className={selectClassName}
          defaultValue={defaultValue}
          inputProps={{
            tabIndex: tabIndexVal,
          }}
          inputRef={inputRefObj}
          disabled={disabled != undefined ? disabled : isEditable}
          error={showError ? (errors ? true : false) : false}
          fullWidth
          onBlur={onBlur}
          onChange={(event: any) => {
            const valueArr = event.target.value as string[];
            onValueChange(valueArr);
          }}
          renderValue={(selected) =>
            (selected as string[]).length === 0 ? (
              placeHolder || defaultOptionLabel || 'Select'
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(selected as string[]).map((val) => {
                  const opt = optionsList.find((o) => o.value === val);
                  return (
                    <Chip
                      key={val}
                      label={opt ? opt.label : val}
                      size="small"
                      style={{
                        backgroundColor: '#DEC330',
                        color: '#222',
                        fontWeight: 600,
                        margin: 2,
                      }}
                    />
                  );
                })}
              </div>
            )
          }>
          {optionsList &&
            optionsList.length &&
            (sortByOptionDisable
              ? optionsList
              : optionsList.sort((a, b) =>
                  a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1,
                )
            ).map((ele, index) => (
              <MenuItem
                key={ele.value}
                value={ele.value}
                disabled={ele.disabled}
                className={
                  ele.optionClassName
                    ? ele.optionClassName
                    : classes.optionsClassName
                }>
                <Checkbox
                  checked={inputVal.indexOf(ele.value) > -1}
                  size="small"
                />
                <ListItemText primary={ele.label} />
              </MenuItem>
            ))}
        </Select>
        {errors && (
          <FormHelperText className={classes.helperText}>
            {errors}
          </FormHelperText>
        )}
      </>
    );
  },
);

const useStyles = makeStyles((theme) =>
  createStyles({
    helperText: {
      color: `${theme.palette.warning.main} !important`,
      marginLeft: 'unset !important',
      position: 'relative',
    },
    optionsClassName: {
      backgroundColor: `${theme.palette.common.white} !important`,
      paddingLeft: '10px !important',
      paddingRight: '27px !important',
      textTransform: 'capitalize',
    },
  }),
);
